/**
 * ============================================================================
 * LISTDETAIL.JS v14.0 - PWA OFFLINE SUPPORT
 * ============================================================================
 *
 * ✅ v14.0: PWA OFFLINE SUPPORT
 *           - Offline Item-Toggle mit IndexedDB
 *           - Sync-Queue für Offline-Änderungen
 *           - Offline-Indikator im Header
 * ✅ v13.0: CALENDAR INTEGRATION
 *           - "Zum Kalender hinzufügen" Button im Header
 *           - Erstellt Kalendereintrag für Einkaufsliste
 *           - Verknüpft via source_shopping_list_id
 * ✅ v12.0: PRODUCT PICKER
 *           - Neuer "Artikel auswählen" Button
 *           - ProductPicker Modal für Artikelauswahl statt Texteingabe
 *           - User kann aus vordefiniertem Katalog wählen
 * ✅ v11.0: PLANIVIO BRANDING
 *           - Orange → Teal (#4A90A4) / Green (#6BA893)
 *           - Akzentfarbe: #E8976C (Warm Orange)
 *           - Konsistentes Design mit App.js v43
 * ✅ v10.6: NEUER RAG WORKFLOW - homem-preisvergleich Endpoint
 * ✅ v10.5: HORIZONTALES ADD-FORMULAR
 * ✅ v10.4: CHAT-LESBARKEIT verbessert
 *
 * @version 14.0.0 - PWA Offline Support
 */

import React, { useState, useRef, useMemo } from 'react';
import { useHousehold } from '../../context/HouseholdContext';
import { useToken } from '../../context/TokenContext';
import { useOffline } from '../../context/OfflineContext'; // ✅ v14: PWA Offline
import { indexedDBService } from '../../services/indexedDB.service'; // ✅ v14: IndexedDB
import ProductPicker from './ProductPicker';
import { invalidateCalendarCache } from '../calendar/calendarCache';
import Preisvergleich from '../Preisvergleich/Preisvergleich';
import './Shopping.css';

// ✅ v14.1: Backend-Proxy für N8N (kein CORS-Problem)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const N8N_PROXY_URL = `${API_BASE_URL}/api/n8n/preisvergleich`;

const getStoredMode = () => {
  try {
    const stored = localStorage.getItem('n8n_webhook_mode');
    if (stored === 'test' || stored === 'prod') {
      return stored === 'test';
    }
  } catch (e) {}
  return false;
};

const CATEGORIES = [
  'Obst & Gemüse', 'Milchprodukte', 'Fleisch & Fisch', 
  'Brot & Backwaren', 'Getränke', 'Haushalt', 'Sonstiges'
];

const UNITS = ['Stück', 'g', 'kg', 'L', 'ml', 'Packung', 'Dose', 'Bund'];

console.log('🛒 ListDetail.js v14.0 loaded - PWA OFFLINE SUPPORT 📴');

// ✅ v11.0: PLANIVIO COLORS
const colors = {
  primary: '#4A90A4',      // Warm Blue
  secondary: '#6BA893',    // Soft Green
  accent: '#E8976C',       // Warm Orange
  textPrimary: '#2D3E50',
  textSecondary: '#5A6B7A',
  border: '#B8D4DC',
  borderLight: '#D4E8EC',
  bgLight: '#E8F4F8',
  bgCard: '#F0F9FF',
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#EF4444'
};

// Helper zum Bereinigen der Bot-Antwort
const cleanBotResponse = (text) => {
  if (!text) return 'Keine Antwort erhalten';

  return text
    .replace(/^```[\w]*\n?/gm, '')
    .replace(/\n?```$/gm, '')
    .trim();
};

// ✅ v14.1: Preisvergleich-Ergebnis Komponente — gruppiert nach Laden
const PreisvergleichErgebnis = ({ data }) => {
  if (!data || !data.success) return null;

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: '1.6', width: '100%' }}>

      {/* Pro Laden */}
      {data.ladenRanking && data.ladenRanking.map(lr => (
        <div key={lr.laden} style={{ marginBottom: '14px' }}>
          <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px', color: '#1F2937' }}>
            🏪 {lr.ladenDisplay}
          </div>
          {(data.nachLaden?.[lr.laden] || []).map((a, i) => (
            <div key={i} style={{ paddingLeft: '14px', color: '#374151', display: 'flex', justifyContent: 'space-between' }}>
              <span>{a.artikel}</span>
              <span>
                {a.preisFormatiert}
                {a.istAngebot && (
                  <span style={{ color: '#059669', marginLeft: '6px', fontSize: '11px', fontWeight: 600 }}>
                    🏷️ -{a.rabattProzent}%
                  </span>
                )}
              </span>
            </div>
          ))}
          <div style={{
            paddingLeft: '14px',
            marginTop: '4px',
            paddingTop: '4px',
            borderTop: '1px solid #DDD6FE',
            fontWeight: 600,
            color: '#1F2937',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span>Summe</span>
            <span>{lr.summeFormatiert} ({lr.abdeckung}/{data.artikelGesucht} Artikel)</span>
          </div>
        </div>
      ))}

      {/* Auswertung */}
      <div style={{ borderTop: '2px solid #DDD6FE', paddingTop: '12px', marginTop: '4px' }}>
        <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '6px', color: '#1F2937' }}>
          📊 Auswertung
        </div>

        {data.ladenRanking && data.ladenRanking.map((lr, i) => (
          <div key={lr.laden} style={{ display: 'flex', justifyContent: 'space-between', color: '#374151' }}>
            <span>{medals[i] || `${lr.rang}.`} {lr.ladenDisplay}</span>
            <span>{lr.summeFormatiert} ({lr.abdeckungProzent}%)</span>
          </div>
        ))}

        {data.guenstigsterWarenkorb && (
          <div style={{ marginTop: '6px', fontWeight: 600, color: '#059669', display: 'flex', justifyContent: 'space-between' }}>
            <span>💰 Cherry-Pick</span>
            <span>{data.guenstigsterWarenkorb.summeFormatiert}</span>
          </div>
        )}

        {data.maxErsparnis > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#374151' }}>
            <span>📈 Ersparnis</span>
            <span>{data.maxErsparnisFormatiert}</span>
          </div>
        )}

        {data.empfehlung && (
          <div style={{ marginTop: '6px', fontWeight: 600, color: '#1F2937' }}>
            🏆 {data.empfehlung}
          </div>
        )}

        {data.nichtGefunden && data.nichtGefunden.length > 0 && (
          <div style={{ marginTop: '8px', color: '#DC2626', fontSize: '13px' }}>
            ❌ Nicht gefunden: {data.nichtGefunden.join(', ')}
          </div>
        )}

        <div style={{ marginTop: '10px', fontSize: '11px', color: '#9CA3AF' }}>
          Stand: {new Date().toLocaleDateString('de-DE')} | Ohne Gewähr
        </div>
      </div>
    </div>
  );
};

const ListDetail = ({ initialData, onClose, onSave, onDelete }) => {

  // ============================================================================
  // CONTEXT
  // ============================================================================
  const { getMemberOptions, members } = useHousehold();
  const { token } = useToken();
  const { isOnline, addToQueue } = useOffline(); // ✅ v14: Offline-Status

  const FAMILY_MEMBERS = useMemo(() => {
    const options = getMemberOptions(true);
    return options;
  }, [members, getMemberOptions]);

  // ============================================================================
  // STATE
  // ============================================================================
  
  const [items, setItems] = useState(() => {
    console.log('🎯 ListDetail mounted - items:', initialData?.items?.length || 0);
    return [...(initialData?.items || [])];
  });
  
  const [name, setName] = useState(initialData?.name || 'Unbenannte Liste');
  const [assignedTo, setAssignedTo] = useState(initialData?.assignedTo || 'alle');
  
  const originalRef = useRef({
    items: JSON.stringify(initialData?.items || []),
    name: initialData?.name,
    assignedTo: initialData?.assignedTo || 'alle',
    weekMenu: JSON.stringify(initialData?.weekMenu || {})
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(initialData?.name || '');
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '', quantity: '', unit: 'Stück', category: 'Obst & Gemüse'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('Alle');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ✅ v12.0: Product Picker State
  const [showProductPicker, setShowProductPicker] = useState(false);

  // ✅ v16.0: Preisvergleich Overlay State
  const [showPreisvergleich, setShowPreisvergleich] = useState(false);

  // ✅ v15.0: Wochenmenü State
  const [weekMenu, setWeekMenu] = useState(() => initialData?.weekMenu || {
    montag: '',
    dienstag: '',
    mittwoch: '',
    donnerstag: '',
    freitag: '',
    samstag: '',
    sonntag: ''
  });
  const [showWeekMenu, setShowWeekMenu] = useState(false);

  // ✅ v13.0: Calendar Integration State
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarDate, setCalendarDate] = useState(() => {
    // Default: Heute oder dueDate der Liste
    if (initialData?.dueDate) return initialData.dueDate;
    return new Date().toISOString().split('T')[0];
  });
  const [calendarTime, setCalendarTime] = useState('10:00');
  const [isAddingToCalendar, setIsAddingToCalendar] = useState(false);

  const [useTestWebhook, setUseTestWebhook] = useState(getStoredMode);

  const webhookMode = useTestWebhook ? 'test' : 'prod';

  const handleToggleWebhook = () => {
    const newValue = !useTestWebhook;
    setUseTestWebhook(newValue);
    try {
      localStorage.setItem('n8n_webhook_mode', newValue ? 'test' : 'prod');
    } catch (e) {}
    console.log('🔄 N8N Mode switched to:', newValue ? 'TEST' : 'PROD');
  };

  // ============================================================================
  // COMPUTED
  // ============================================================================
  
  const hasChanges =
    JSON.stringify(items) !== originalRef.current.items ||
    name !== originalRef.current.name ||
    assignedTo !== originalRef.current.assignedTo ||
    JSON.stringify(weekMenu) !== originalRef.current.weekMenu;

  const stats = {
    total: items.length,
    checked: items.filter(i => i.checked).length,
    unchecked: items.filter(i => !i.checked).length,
    progress: items.length > 0 
      ? Math.round((items.filter(i => i.checked).length / items.length) * 100) 
      : 0
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = !searchTerm || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'Alle' || 
      item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleClose = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }

    if (hasChanges) {
      // Auto-Save bei Schließen
      setIsSaving(true);
      const updatedList = {
        ...initialData,
        name: name,
        items: items,
        assignedTo: assignedTo,
        weekMenu: weekMenu
      };
      console.log('💾 Auto-Saving', items.length, 'items, assignedTo:', assignedTo, 'weekMenu:', weekMenu);
      onSave?.(updatedList);
    } else {
      onClose?.();
    }
  };

  // ✅ v14: handleToggleItem mit Offline-Support
  const handleToggleItem = async (itemId) => {
    console.log('☑️ Toggle:', itemId, isOnline ? '(online)' : '(offline)');

    // Optimistic UI Update
    const newItems = items.map(item =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    );
    setItems(newItems);

    // ✅ v14: Bei Offline in IndexedDB speichern und Sync-Queue hinzufügen
    if (!isOnline) {
      console.log('📴 Offline - saving item toggle to IndexedDB');
      try {
        // Aktualisierte Liste in IndexedDB speichern
        await indexedDBService.updateShoppingItem(initialData?.id, itemId, !items.find(i => i.id === itemId)?.checked);
        console.log('💾 Item toggle saved to IndexedDB');

        // Zur Sync-Queue hinzufügen (nur wenn echte Liste-ID)
        if (initialData?.id && !String(initialData.id).startsWith('temp-')) {
          await addToQueue({
            type: 'shopping',
            operation: 'update',
            endpoint: `/api/shopping/${initialData.id}`,
            method: 'PUT',
            payload: {
              name: name,
              description: initialData?.description,
              items: newItems,
              priority: initialData?.priority,
              assignedTo: assignedTo,
              dueDate: initialData?.dueDate
            }
          });
          console.log('📤 Item toggle queued for sync');
        }
      } catch (err) {
        console.warn('⚠️ Offline save failed:', err.message);
      }
    }
  };

  const handleAddItem = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!newItem.name.trim()) return;
    
    const item = {
      id: 'item-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      name: newItem.name.trim(),
      quantity: parseFloat(newItem.quantity) || 1,
      unit: newItem.unit,
      category: newItem.category,
      checked: false
    };
    
    setItems(prev => [...prev, item]);
    setNewItem({ name: '', quantity: '', unit: 'Stück', category: 'Obst & Gemüse' });
    setShowAddItem(false);
    return false;
  };

  const handleDeleteItem = (itemId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setItems(prev => prev.filter(item => item.id !== itemId));
    return false;
  };

  const handleUpdateName = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (editedName.trim()) setName(editedName.trim());
    setIsEditing(false);
    return false;
  };

  const handleCheckAll = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setItems(prev => prev.map(item => ({ ...item, checked: true })));
    return false;
  };

  const handleUncheckAll = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setItems(prev => prev.map(item => ({ ...item, checked: false })));
    return false;
  };

  const handleDeleteChecked = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!window.confirm('Alle erledigten Artikel löschen?')) return false;
    setItems(prev => prev.filter(item => !item.checked));
    return false;
  };

  // ✅ v12.0: Handler für ProductPicker - Mehrere Artikel hinzufügen
  const handleAddItemsFromPicker = (newItems) => {
    console.log('📦 Adding items from ProductPicker:', newItems.length);
    setItems(prev => [...prev, ...newItems]);
  };

  const handleDeleteList = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (window.confirm('Liste "' + name + '" wirklich löschen?')) {
      onDelete?.(initialData?.id);
    }
    return false;
  };

  // ✅ v13.0: Kalender-Eintrag erstellen
  const handleAddToCalendar = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }

    if (!token) {
      alert('Bitte zuerst einloggen');
      return;
    }

    setIsAddingToCalendar(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      // Kalendereintrag erstellen
      const calendarData = {
        title: `🛒 ${name}`,
        date: calendarDate,
        time: calendarTime,
        category: 'einkaufen',
        description: `Einkaufsliste: ${items.length} Artikel`,
        sourceShoppingListId: initialData?.id,
        person: assignedTo || 'alle'
      };

      console.log('📅 Creating calendar entry for shopping list:', calendarData);

      const response = await fetch(`${API_BASE_URL}/api/calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(calendarData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Calendar entry created:', result);

      // Cache invalidieren
      invalidateCalendarCache();

      // Modal schließen und Erfolg anzeigen
      setShowCalendarModal(false);
      alert('✅ Einkaufstermin wurde zum Kalender hinzugefügt!');

    } catch (error) {
      console.error('❌ Error creating calendar entry:', error);
      alert('Fehler beim Erstellen des Kalendereintrags: ' + error.message);
    } finally {
      setIsAddingToCalendar(false);
    }
  };

  // ============================================================================
  // BOT FUNCTIONS
  // ============================================================================
  
  const sendToBot = async () => {
    setIsSending(true);
    setShowChat(true);
    setChatMessages(prev => [...prev, {
      id: Date.now(), type: 'user',
      text: '📋 Liste "' + name + '" mit ' + items.length + ' Artikeln gesendet\n🔍 Starte Preisvergleich...'
    }]);

    console.log('🤖 Sending to N8N RAG Workflow:', useTestWebhook ? 'TEST' : 'PROD');

    try {
      const response = await fetch(N8N_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mode: webhookMode,
          Komponent: 'Shopping_Chat',
          listId: initialData?.id,
          listName: name,
          items: items,
          stats: stats,
          timestamp: new Date().toISOString()
        })
      });

      const data = await response.text();
      let botResponse = '';
      let preisvergleichData = null;

      try {
        const jsonData = JSON.parse(data);
        if (jsonData.success === true && jsonData.nachLaden) {
          // ✅ Strukturierter Preisvergleich-Response
          preisvergleichData = jsonData;
        } else {
          const rawResponse = jsonData.result || jsonData.output || jsonData.message || jsonData.response || data;
          botResponse = cleanBotResponse(rawResponse);
        }
      } catch {
        botResponse = cleanBotResponse(data) || 'Antwort erhalten';
      }

      setChatMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: botResponse, preisvergleichData }]);
    } catch (error) {
      console.error('❌ N8N Error:', error);
      setChatMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        text: '❌ Fehler: ' + error.message + '\n\n💡 Tipp: Versuche den ' + (useTestWebhook ? 'PROD' : 'TEST') + ' Mode!'
      }]);
    } finally {
      setIsSending(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    setChatMessages(prev => [...prev, { id: Date.now(), type: 'user', text: chatInput }]);
    const question = chatInput;
    setChatInput('');
    setIsSending(true);

    try {
      const response = await fetch(N8N_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mode: webhookMode,
          Komponent: 'Shopping_Chat',
          listId: initialData?.id,
          listName: name,
          question: question,
          items: items,
          stats: stats,
          timestamp: new Date().toISOString()
        })
      });

      const data = await response.text();
      let botResponse;
      
      try {
        const jsonData = JSON.parse(data);
        const rawResponse = jsonData.result || jsonData.output || jsonData.message || jsonData.response || data;
        botResponse = cleanBotResponse(rawResponse);
      } catch {
        botResponse = cleanBotResponse(data) || 'Antwort erhalten';
      }

      setChatMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: botResponse }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        id: Date.now() + 1, 
        type: 'bot', 
        text: '❌ Fehler: ' + error.message 
      }]);
    } finally {
      setIsSending(false);
    }
  };

  // ============================================================================
  // STYLES - ✅ v11.0: PLANIVIO DESIGN
  // ============================================================================
  
  const styles = {
    // Layout
    mainLayout: { 
      display: 'grid', 
      gridTemplateColumns: showChat ? '1fr 450px' : '1fr',
      gap: '20px' 
    },
    
    // Button Row - ✅ COMPACT
    buttonRow: {
      display: 'flex',
      gap: '8px',
      marginBottom: '12px',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    // ✅ v11.0: Planivio Gradient - COMPACT
    saveButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: `0 2px 8px rgba(74, 144, 164, 0.3)`,
      transition: 'all 0.2s'
    },
    discardButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      background: '#F3F4F6',
      color: '#6B7280',
      border: '1px solid #E5E7EB',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      cursor: 'pointer'
    },
    changesBadge: {
      padding: '4px 10px',
      background: colors.bgLight,
      color: colors.primary,
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 600,
      border: `1px solid ${colors.border}`
    },
    botButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      background: 'linear-gradient(135deg, #8B5CF6, #A855F7)',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      cursor: 'pointer',
      marginLeft: 'auto'
    },
    
    // List Container - ✅ v11.0: Planivio Border
    listContainer: { 
      background: 'white', 
      borderRadius: '16px', 
      border: `2px solid ${colors.border}`, 
      overflow: 'hidden' 
    },
    
    // Header - ✅ COMPACT
    header: {
      padding: '8px 12px',
      background: '#f3f4f6',
      borderBottom: `1px solid ${colors.border}`
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '6px'
    },
    title: {
      margin: 0,
      fontSize: '0.95rem',
      fontWeight: 600,
      color: '#6b7280'
    },
    titleInput: {
      fontSize: '0.95rem',
      fontWeight: 600,
      border: `1.5px solid ${colors.border}`,
      borderRadius: '6px',
      padding: '4px 10px',
      flex: 1
    },
    iconBtn: {
      background: 'none',
      border: 'none',
      fontSize: '1rem',
      cursor: 'pointer',
      padding: '2px 6px',
      color: '#9ca3af',
      transition: 'color 0.2s'
    },
    meta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      alignItems: 'center'
    },
    badge: {
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.7rem',
      fontWeight: 600
    },
    personSelect: {
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 600,
      background: 'white',
      color: '#4B5563',
      border: `1px solid ${colors.border}`,
      cursor: 'pointer',
      minWidth: '100px'
    },

    // ✅ v15.0: Wochenmenü Styles
    weekMenuSection: {
      borderBottom: `1px solid ${colors.border}`,
      background: colors.bgCard
    },
    weekMenuToggle: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: 600,
      color: colors.textPrimary
    },
    weekMenuGrid: {
      display: 'flex',
      gap: '4px',
      padding: '0 8px 8px 8px',
      overflowX: 'auto'
    },
    weekMenuDay: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2px',
      flex: '1 1 0',
      minWidth: '45px'
    },
    weekMenuLabel: {
      fontSize: '0.65rem',
      fontWeight: 700,
      color: colors.primary,
      textTransform: 'uppercase'
    },
    weekMenuInput: {
      width: '100%',
      padding: '4px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.7rem',
      textAlign: 'center',
      background: 'white'
    },

    // Kompakte Toolbar - ✅ COMPACT
    compactToolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 12px',
      borderBottom: `1px solid ${colors.border}`,
      background: colors.bgLight,
      flexWrap: 'wrap'
    },
    statsGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    statBadge: {
      padding: '2px 6px',
      borderRadius: '6px',
      fontSize: '0.7rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '2px'
    },
    progressGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      minWidth: '80px'
    },
    progressBar: {
      width: '60px',
      height: '4px',
      background: colors.borderLight,
      borderRadius: '2px',
      overflow: 'hidden'
    },
    // ✅ v11.0: Planivio Gradient Progress
    progressFill: {
      height: '100%',
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      transition: 'width 0.2s ease'
    },
    progressText: {
      fontSize: '0.7rem',
      fontWeight: 700,
      color: colors.primary,
      minWidth: '30px'
    },
    toolbarDivider: {
      width: '1px',
      height: '18px',
      background: colors.border
    },
    searchInput: {
      width: '120px',
      padding: '4px 8px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.75rem',
      background: 'white'
    },
    categorySelect: {
      padding: '4px 6px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.75rem',
      background: 'white',
      maxWidth: '120px'
    },
    bulkBtnGroup: {
      display: 'flex',
      gap: '3px'
    },
    bulkBtn: {
      padding: '4px 8px',
      background: 'white',
      border: '1px solid #E5E7EB',
      borderRadius: '4px',
      fontSize: '0.7rem',
      cursor: 'pointer',
      fontWeight: 500,
      transition: 'all 0.15s'
    },
    bulkBtnDanger: {
      color: '#DC2626',
      borderColor: '#FECACA'
    },
    
    // Add Section - ✅ COMPACT
    addSection: {
      padding: '8px 12px',
      borderBottom: `1px solid ${colors.border}`,
      background: colors.bgCard
    },
    addBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      cursor: 'pointer',
      width: '100%',
      justifyContent: 'center',
      boxShadow: `0 2px 8px rgba(74, 144, 164, 0.3)`,
      transition: 'all 0.2s'
    },
    // ✅ v12.0: Secondary Button für manuelle Eingabe - COMPACT
    addBtnSecondary: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      background: 'white',
      color: colors.primary,
      border: `1.5px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      cursor: 'pointer',
      width: '100%',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    addForm: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      flexWrap: 'wrap'
    },
    input: {
      flex: 1,
      minWidth: '120px',
      padding: '6px 10px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.8rem'
    },
    inputName: {
      flex: 2,
      minWidth: '140px',
      padding: '6px 10px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.8rem'
    },
    inputQty: {
      width: '55px',
      padding: '6px 8px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.8rem',
      textAlign: 'center'
    },
    select: {
      padding: '6px 8px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.8rem',
      background: 'white'
    },
    selectUnit: {
      width: '70px',
      padding: '6px 6px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.75rem',
      background: 'white'
    },
    selectCategory: {
      width: '100px',
      padding: '6px 6px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '0.75rem',
      background: 'white'
    },
    cancelBtn: {
      padding: '6px 10px',
      background: '#F3F4F6',
      border: '1px solid #E5E7EB',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.75rem'
    },
    submitBtn: {
      padding: '6px 10px',
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap'
    },
    
    // Items List - ✅ COMPACT
    itemsList: {
      padding: 0,
      maxHeight: '400px',
      overflowY: 'auto'
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      borderBottom: `1px solid ${colors.borderLight}`,
      transition: 'background 0.15s'
    },
    itemChecked: {
      background: '#F0FDF4'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer',
      accentColor: colors.success
    },
    itemName: {
      flex: 1,
      fontSize: '0.85rem',
      fontWeight: 500,
      color: colors.textPrimary
    },
    itemNameChecked: {
      textDecoration: 'line-through',
      color: '#9CA3AF'
    },
    itemMeta: {
      display: 'flex',
      gap: '6px',
      alignItems: 'center',
      fontSize: '0.7rem',
      color: '#6B7280'
    },
    itemCategory: {
      padding: '1px 6px',
      background: '#F3F4F6',
      borderRadius: '4px',
      fontSize: '0.65rem'
    },
    deleteItemBtn: {
      background: 'none',
      border: 'none',
      fontSize: '0.85rem',
      cursor: 'pointer',
      padding: '2px',
      opacity: 0.4,
      transition: 'opacity 0.15s'
    },
    emptyState: {
      padding: '40px 20px',
      textAlign: 'center',
      color: '#9CA3AF'
    },
    
    // Chat Panel
    chatPanel: { 
      background: 'white', 
      borderRadius: '16px', 
      border: '2px solid #A855F7', 
      display: 'flex', 
      flexDirection: 'column', 
      height: '650px',
      overflow: 'hidden' 
    },
    chatHeader: { 
      padding: '12px 16px', 
      background: 'linear-gradient(135deg, #8B5CF6, #A855F7)', 
      color: 'white', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px' 
    },
    chatMessages: { 
      flex: 1, 
      padding: '16px', 
      overflowY: 'auto', 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '16px',
      background: '#F9FAFB'
    },
    messageBot: { 
      background: 'linear-gradient(135deg, #F3E8FF 0%, #EDE9FE 100%)',
      color: '#1F2937',
      alignSelf: 'stretch',
      width: '100%',
      maxWidth: '100%',
      padding: '16px 20px',
      borderRadius: '12px',
      whiteSpace: 'pre-wrap',
      fontFamily: "'SF Mono', 'Consolas', 'Monaco', 'Menlo', monospace",
      fontSize: '0.8rem',
      lineHeight: '1.7',
      border: '1px solid #DDD6FE',
      boxShadow: '0 2px 8px rgba(139, 92, 246, 0.1)'
    },
    // ✅ v11.0: User Message mit Planivio Gradient
    messageUser: { 
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, 
      color: 'white', 
      alignSelf: 'flex-end',
      maxWidth: '80%',
      padding: '12px 16px',
      borderRadius: '12px',
      fontSize: '0.85rem',
      lineHeight: '1.5',
      fontFamily: 'inherit'
    },
    chatInputRow: { 
      padding: '12px 16px', 
      borderTop: '1px solid #E9D5FF', 
      display: 'flex', 
      gap: '8px',
      background: 'white'
    },
    chatInput: { 
      flex: 1, 
      padding: '12px 16px', 
      border: '2px solid #E9D5FF', 
      borderRadius: '10px', 
      fontSize: '0.9rem',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    chatSendBtn: { 
      padding: '12px 20px', 
      background: 'linear-gradient(135deg, #8B5CF6, #A855F7)', 
      color: 'white', 
      border: 'none', 
      borderRadius: '10px', 
      fontWeight: 600, 
      cursor: 'pointer',
      transition: 'transform 0.1s'
    },
    
    // N8N Toggle
    n8nToggleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      background: 'rgba(255,255,255,0.15)',
      borderRadius: '8px',
      marginLeft: 'auto'
    },
    n8nToggleLabel: {
      fontSize: '0.7rem',
      opacity: 0.9
    },
    n8nToggleSwitch: {
      position: 'relative',
      width: '50px',
      height: '24px',
      background: useTestWebhook ? '#FCD34D' : '#10B981',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'background 0.2s',
      border: 'none',
      padding: 0
    },
    n8nToggleKnob: {
      position: 'absolute',
      top: '2px',
      left: useTestWebhook ? '2px' : '26px',
      width: '20px',
      height: '20px',
      background: 'white',
      borderRadius: '50%',
      transition: 'left 0.2s',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    },
    n8nModeText: {
      fontSize: '0.7rem',
      fontWeight: 700,
      minWidth: '35px',
      textAlign: 'center',
      background: useTestWebhook ? '#FCD34D' : '#10B981',
      color: useTestWebhook ? '#92400E' : 'white',
      padding: '2px 6px',
      borderRadius: '4px'
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div>
      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 900px) {
          .main-layout-grid {
            grid-template-columns: 1fr !important;
          }
          .chat-panel-responsive {
            height: 500px !important;
          }
        }
        @media (max-width: 768px) {
          .compact-toolbar {
            flex-direction: row !important;
            flex-wrap: wrap !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 6px !important;
            padding: 8px !important;
          }
          .toolbar-divider {
            display: none;
          }
          .stats-group, .bulk-btn-group {
            flex-shrink: 0;
          }
        }
      `}</style>

      {/* Button Row */}
      <div style={styles.buttonRow}>
        <button
          type="button"
          style={styles.saveButton}
          onClick={handleClose}
          disabled={isSaving}
        >
          {isSaving ? '⏳ Speichere...' : '← Zurück'}
        </button>
        
        <button type="button" style={styles.botButton} onClick={() => setShowPreisvergleich(true)}>
          🔍 Preisvergleich
        </button>
      </div>

      {/* Main Layout */}
      <div style={styles.mainLayout} className="main-layout-grid">
        
        {/* Shopping List */}
        <div style={styles.listContainer}>
          
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.titleRow}>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    style={styles.titleInput}
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleUpdateName(e); }
                    }}
                  />
                  <button type="button" style={styles.iconBtn} onClick={handleUpdateName}>✅</button>
                  <button type="button" style={styles.iconBtn} onClick={() => { setEditedName(name); setIsEditing(false); }}>❌</button>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '1rem' }}>🛒</span>
                  <h1 style={styles.title}>{name}</h1>
                  <button type="button" style={styles.iconBtn} onClick={() => { setEditedName(name); setIsEditing(true); }} title="Umbenennen">✏️</button>
                  <button type="button" style={styles.iconBtn} onClick={() => setShowCalendarModal(true)} title="Zum Kalender hinzufügen">📅</button>
                  <button type="button" style={styles.iconBtn} onClick={handleDeleteList} title="Liste löschen">🗑️</button>
                </>
              )}
            </div>
            <div style={styles.meta}>
              <select
                style={styles.personSelect}
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
              >
                {FAMILY_MEMBERS.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.avatar} {member.name}
                  </option>
                ))}
              </select>
              
              {/* ✅ v14: Offline-Indikator */}
              {!isOnline && (
                <span style={{
                  ...styles.badge,
                  background: '#FEF3C7',
                  color: '#D97706',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  📴 Offline
                </span>
              )}
            </div>
          </div>

          {/* ✅ v15.0: Wochenmenü */}
          <div style={styles.weekMenuSection}>
            <button
              type="button"
              style={styles.weekMenuToggle}
              onClick={() => setShowWeekMenu(!showWeekMenu)}
            >
              <span>🍽️ Wochenmenü</span>
              <span style={{ transform: showWeekMenu ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
            </button>

            {showWeekMenu && (
              <div style={styles.weekMenuGrid}>
                {['montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag', 'sonntag'].map((day) => (
                  <div key={day} style={styles.weekMenuDay}>
                    <label style={styles.weekMenuLabel}>
                      {day.charAt(0).toUpperCase() + day.slice(1, 2)}
                    </label>
                    <input
                      type="text"
                      placeholder={day.charAt(0).toUpperCase() + day.slice(1)}
                      value={weekMenu[day]}
                      onChange={(e) => setWeekMenu(prev => ({ ...prev, [day]: e.target.value }))}
                      style={styles.weekMenuInput}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Kompakte Toolbar - ✅ v11.0: Planivio Colors */}
          <div style={styles.compactToolbar} className="compact-toolbar">
            
            {/* Stats */}
            <div style={styles.statsGroup} className="stats-group">
              <span style={{...styles.statBadge, background: colors.bgCard, color: colors.primary, border: `1px solid ${colors.border}`}}>
                📦 {stats.total}
              </span>
              <span style={{...styles.statBadge, background: '#FEF3C7', color: '#D97706', border: '1px solid #FCD34D'}}>
                ⏳ {stats.unchecked}
              </span>
              <span style={{...styles.statBadge, background: '#D1FAE5', color: '#059669', border: '1px solid #6EE7B7'}}>
                ✓ {stats.checked}
              </span>
            </div>
            
            {/* Progress */}
            <div style={styles.progressGroup}>
              <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: stats.progress + '%'}}></div>
              </div>
              <span style={styles.progressText}>{stats.progress}%</span>
            </div>
            
            <div style={styles.toolbarDivider} className="toolbar-divider"></div>

            {/* Category */}
            <select 
              value={filterCategory} 
              onChange={(e) => setFilterCategory(e.target.value)} 
              style={styles.categorySelect}
            >
              <option value="Alle">Alle</option>
              {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            
            <div style={styles.toolbarDivider} className="toolbar-divider"></div>
            
            {/* Bulk Actions */}
            <div style={styles.bulkBtnGroup} className="bulk-btn-group">
              <button type="button" style={styles.bulkBtn} onClick={handleCheckAll} title="Alle abhaken">
                ✓
              </button>
              <button type="button" style={styles.bulkBtn} onClick={handleUncheckAll} title="Alle zurücksetzen">
                ☐
              </button>
              {stats.checked > 0 && (
                <button 
                  type="button" 
                  style={{...styles.bulkBtn, ...styles.bulkBtnDanger}} 
                  onClick={handleDeleteChecked}
                  title="Erledigte löschen"
                >
                  🗑️ {stats.checked}
                </button>
              )}
            </div>
          </div>

          {/* Add Item - ✅ v12.0: Zwei Optionen */}
          <div style={styles.addSection}>
            {!showAddItem ? (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {/* ✅ v12.0: NEU - Artikel aus Katalog auswählen */}
                <button
                  type="button"
                  style={{...styles.addBtn, flex: 1, minWidth: '200px'}}
                  onClick={() => setShowProductPicker(true)}
                >
                  📦 Artikel auswählen
                </button>
                {/* Manuell hinzufügen (fallback) */}
                <button
                  type="button"
                  style={{...styles.addBtnSecondary, flex: 1, minWidth: '200px'}}
                  onClick={() => setShowAddItem(true)}
                >
                  ✏️ Manuell eingeben
                </button>
              </div>
            ) : (
              <div style={styles.addForm}>
                <input
                  type="text"
                  placeholder="Artikel Name..."
                  value={newItem.name}
                  onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                  style={styles.inputName}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newItem.name.trim()) {
                      e.preventDefault();
                      handleAddItem(e);
                    }
                  }}
                />
                <input
                  type="number"
                  placeholder="1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({...newItem, quantity: e.target.value})}
                  style={styles.inputQty}
                />
                <select
                  value={newItem.unit}
                  onChange={(e) => setNewItem({...newItem, unit: e.target.value})}
                  style={styles.selectUnit}
                >
                  {UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                </select>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                  style={styles.selectCategory}
                >
                  {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowAddItem(false)}>✕</button>
                <button type="button" style={styles.submitBtn} onClick={handleAddItem} disabled={!newItem.name.trim()}>
                  ➕ Hinzufügen
                </button>
              </div>
            )}
          </div>

          {/* Items List */}
          <div style={styles.itemsList}>
            {filteredItems.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={{fontSize: '2rem', marginBottom: '8px'}}>📝</p>
                <p style={{fontSize: '0.85rem'}}>Keine Artikel gefunden</p>
              </div>
            ) : (
              filteredItems.map(item => (
                <div 
                  key={item.id} 
                  style={{...styles.item, ...(item.checked ? styles.itemChecked : {})}}
                >
                  <input
                    type="checkbox"
                    checked={item.checked || false}
                    onChange={() => handleToggleItem(item.id)}
                    style={styles.checkbox}
                  />
                  <span style={{...styles.itemName, ...(item.checked ? styles.itemNameChecked : {})}}>
                    {item.name}
                  </span>
                  <div style={styles.itemMeta}>
                    <span>{item.quantity} {item.unit}</span>
                    <span style={styles.itemCategory}>{item.category}</span>
                  </div>
                  <button
                    type="button"
                    style={styles.deleteItemBtn}
                    onClick={(e) => handleDeleteItem(item.id, e)}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = 0.4}
                  >
                    🗑️
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div style={styles.chatPanel} className="chat-panel-responsive">
            <div style={styles.chatHeader}>
              <span style={{ fontSize: '1.5rem' }}>🤖</span>
              <div>
                <div style={{ fontWeight: 700 }}>Preisvergleich</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>RAG-powered</div>
              </div>
              
              {/* N8N MODE TOGGLE */}
              <div style={styles.n8nToggleContainer}>
                <span style={styles.n8nToggleLabel}>N8N:</span>
                <button 
                  type="button"
                  style={styles.n8nToggleSwitch}
                  onClick={handleToggleWebhook}
                  title={`Aktuell: ${useTestWebhook ? 'TEST' : 'PROD'}`}
                >
                  <div style={styles.n8nToggleKnob}></div>
                </button>
                <span style={styles.n8nModeText}>
                  {useTestWebhook ? 'TEST' : 'PROD'}
                </span>
              </div>
              
              <button 
                type="button"
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: '8px', borderRadius: '8px', cursor: 'pointer', marginLeft: '8px' }}
                onClick={() => setShowChat(false)}
              >✕</button>
            </div>
            
            {/* Chat Messages */}
            <div style={styles.chatMessages}>
              {chatMessages.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', gap: '12px' }}>
                  <span style={{ fontSize: '2rem' }}>💰</span>
                  <span>Klicke auf "Preisvergleich" um zu starten</span>
                  <span style={{ fontSize: '0.75rem', color: '#D1D5DB' }}>
                    Mode: {useTestWebhook ? '🟡 TEST' : '🟢 PROD'}
                  </span>
                </div>
              ) : (
                chatMessages.map(msg => (
                  <div
                    key={msg.id}
                    style={msg.type === 'bot' ? styles.messageBot : styles.messageUser}
                  >
                    {msg.type === 'bot' && !msg.preisvergleichData && (
                      <span style={{ marginRight: '8px' }}>🤖</span>
                    )}
                    {msg.preisvergleichData
                      ? <PreisvergleichErgebnis data={msg.preisvergleichData} />
                      : msg.text
                    }
                  </div>
                ))
              )}
            </div>
            
            {/* Chat Input */}
            <div style={styles.chatInputRow}>
              <input
                type="text"
                placeholder="Frag nach Preisen..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendChatMessage(); } }}
                style={styles.chatInput}
              />
              <button type="button" style={styles.chatSendBtn} onClick={sendChatMessage} disabled={isSending || !chatInput.trim()}>
                📤
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ✅ v12.0: ProductPicker Modal */}
      <ProductPicker
        isOpen={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        onAddItems={handleAddItemsFromPicker}
        existingItems={items}
      />

      {/* ✅ v13.0: Calendar Modal */}
      {showCalendarModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
              📅 Zum Kalender hinzufügen
            </h3>

            <p style={{ fontSize: '0.85rem', color: colors.textSecondary, marginBottom: '16px' }}>
              Erstelle einen Einkaufstermin für <strong>"{name}"</strong>
            </p>

            {/* Datum */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px', color: colors.textSecondary }}>
                Datum
              </label>
              <input
                type="date"
                value={calendarDate}
                onChange={(e) => setCalendarDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1.5px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Uhrzeit */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px', color: colors.textSecondary }}>
                Uhrzeit
              </label>
              <input
                type="time"
                value={calendarTime}
                onChange={(e) => setCalendarTime(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1.5px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Info */}
            <div style={{
              padding: '10px 12px',
              background: colors.bgLight,
              borderRadius: '8px',
              fontSize: '0.75rem',
              color: colors.textSecondary,
              marginBottom: '20px'
            }}>
              📋 {items.length} Artikel · 👤 {assignedTo === 'alle' ? 'Alle' : assignedTo}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setShowCalendarModal(false)}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleAddToCalendar}
                disabled={isAddingToCalendar}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: isAddingToCalendar ? 'wait' : 'pointer',
                  opacity: isAddingToCalendar ? 0.7 : 1
                }}
              >
                {isAddingToCalendar ? '⏳ Erstelle...' : '📅 Hinzufügen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ v16.0: Preisvergleich Overlay */}
      {showPreisvergleich && (
        <Preisvergleich
          list={{ id: initialData?.id, name, items }}
          onClose={() => setShowPreisvergleich(false)}
        />
      )}
    </div>
  );
};

export default ListDetail;